import { Box } from "@mui/material";
import { FieldGroupingType, FilterOption } from "../filterPopover/FilterPopover.types.ts";
import FilterPopover from "../filterPopover/FilterPopover.tsx";
import { DotLegalButton } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useFilterBarData } from "./FilterBar.hooks.ts";
import FilterValueChips from "./FilterValueChips.tsx";
import { isFilterChipDisplayed, nextPinnedFilterIds } from "./filterPinning.ts";
import { IdNamePair } from "../../../customLists/customListView/CustomListView.types.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import ListPageSearchField from "./ListPageSearchField.tsx";
import {
    listPageToolbarIconButton,
    listPageToolbarIconButtonSize,
    listPageToolbarLeftContentSelector,
} from "@common/components/listPageToolbar/ListPageToolbar.styles";
import ResetIcon from "./reset-icon.svg?react";

export interface FilterBarProps {
    isLoading: boolean;
    filterData: Array<FilterOption>;
    fetchFilterData: boolean;
    onMenuClose: (filterOptions: Array<FilterOption>) => void;
    onRemove: (filterOptions: Array<FilterOption>) => void;
    groupingOrder?: Array<FieldGroupingType>;

    onClearFilter?: () => void;
    clearFilterText?: "savedView" | "defaultView" | string;

    searchValue?: string;
    onSearchChange?: (value: string) => void;
    searchLabel?: string;
    disableFilters?: boolean;

    // Ids of filters that are on screen without a value. Emptying a chip leaves it in place so the user can type
    // into it again; only its X removes it. Callers that omit these props opt out of that behaviour entirely.
    pinnedFilterIds?: Array<string>;
    onPinnedFilterIdsChange?: (ids: Array<string> | undefined) => void;
}

function FilterBar(props: FilterBarProps) {
    const {
        isLoading,
        filterData,
        onMenuClose,
        fetchFilterData,
        onClearFilter,
        searchValue,
        onSearchChange,
        searchLabel,
        disableFilters,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = props;

    const { translateString } = useTranslation();
    const { data: filterBarData, isLoading: filterBarIsLoading, setQueryData } = useFilterBarData(filterData, fetchFilterData);

    const handleSave = (updatedFilterOptions: Array<FilterOption>, newFilterBarData: Record<string, IdNamePair[]>) => {
        setQueryData(updatedFilterOptions, newFilterBarData);
        onMenuClose(updatedFilterOptions);
    };

    const handleRemove = (updatedFilterOptions: Array<FilterOption>) => {
        props.onRemove(updatedFilterOptions);
    };

    const hasAnyActiveFilter = filterData.some((x) => isFilterChipDisplayed(x, pinnedFilterIds));
    const displayFilterBarData = !filterBarIsLoading && fetchFilterData && hasAnyActiveFilter;

    const getClearFilterText = () => {
        if (props.clearFilterText === "savedView") {
            return translateString("resetFilterToSavedView");
        }

        if (props.clearFilterText === "defaultView") {
            return translateString("resetToDefaultView");
        }

        return isNullOrWhitespace(props.clearFilterText) ? undefined : props.clearFilterText;
    };

    return (
        <Box
            sx={{
                display: "flex",
                flex: "1 1 auto",
                gap: 1,
                alignItems: "stretch",
                flexWrap: "wrap",
                // Straight in a list page toolbar the parts wrap in the toolbar's filter area, which leaves room for the buttons beside the last row
                [listPageToolbarLeftContentSelector]: { display: "contents", "& > *": { minHeight: 38.5 } },
            }}
        >
            {onSearchChange && <ListPageSearchField value={searchValue ?? ""} onChange={onSearchChange} label={searchLabel} />}

            {!disableFilters && (
                <>
                    <FilterPopover
                        filterBarData={filterBarData}
                        isLoading={filterBarIsLoading}
                        filterData={filterData}
                        pinnedFilterIds={pinnedFilterIds}
                        // The menu's own pin set stands in for the previous one: a filter the user clicked into the bar
                        // counts as already on screen and survives, and one they clicked back out is simply absent.
                        onMenuClose={(updatedFilterOptions, menuPinnedFilterIds) => {
                            onMenuClose(updatedFilterOptions);
                            onPinnedFilterIdsChange?.(nextPinnedFilterIds(filterData, menuPinnedFilterIds, updatedFilterOptions));
                        }}
                        groupingOrder={props.groupingOrder}
                    />

                    {displayFilterBarData && (
                        <FilterValueChips
                            filterOptions={filterData}
                            filterBarData={filterBarData}
                            onSave={handleSave}
                            onRemove={handleRemove}
                            pinnedFilterIds={pinnedFilterIds}
                            onPinnedFilterIdsChange={onPinnedFilterIdsChange}
                        />
                    )}

                    {!isLoading && !filterBarIsLoading && onClearFilter && (
                        <Box sx={{ alignSelf: "center", ...listPageToolbarIconButton }}>
                            <DotLegalButton
                                btnLength={listPageToolbarIconButtonSize}
                                buttonType={"secondary"}
                                onClick={onClearFilter}
                                toolTip={getClearFilterText()}
                            >
                                <ResetIcon />
                            </DotLegalButton>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}

export default FilterBar;
